import { Download, FilterAlt } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Select,
  FormControl,
  InputLabel,
  Stack,
  TextField,
} from "@mui/material";
import {
  LocalizationProvider,
  MobileDatePicker,
  MobileDateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import axios from "axios";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import * as writeXLSX from "xlsx";
import IpinModal from "../IpaisaMpin/IpinModal";
import SetPasswordModal from "../IpaisaMpin/SetIPMpin";

const SERVICE_OPTIONS = [
  { value: "INSTANTPAY", label: "INSTANTPAY" },
  { value: "CASHFREE", label: "CASHFREE" },
];

const ApprovalPayout = () => {
  const [currPage, setCurrPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    searchBy: "",
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [openDownloadWindow, setOpenDownloadWindow] = useState(false);

  // Multi-select state
  const [selectedRefs, setSelectedRefs] = useState([]);

  // IPIN Modal States
  const [isIpinModalOpen, setIpinModalOpen] = useState(false);
  const [openSetPasswordModal, setOpenSetPasswordModal] = useState(false);
  const [pendingActionPayload, setPendingActionPayload] = useState(null);
  const [payoutService, setPayoutService] = useState("");

  // IPIN Handling Functions
  const triggerIpinVerification = (payload) => {
    console.log(
      "[ApprovalPayout] triggerIpinVerification called. Payload:",
      payload
    );
    const isTxnMpinSet = sessionStorage.getItem("isTxnMpinSet");
    console.log("[ApprovalPayout] sessionStorage isTxnMpinSet:", isTxnMpinSet);

    setPendingActionPayload(payload);
    if (isTxnMpinSet === "False") {
      console.log("[ApprovalPayout] Setting openSetPasswordModal to true");
      setOpenSetPasswordModal(true);
    } else {
      console.log("[ApprovalPayout] Setting isIpinModalOpen to true");
      setIpinModalOpen(true);
    }
  };

  const handleIpinVerificationSuccess = async (ipin) => {
    // Assuming 'ipin' is the entered mpin, not directly used here but part of the modal's contract
    console.log(
      "[ApprovalPayout] IPIN verification successful with ipin:",
      ipin
    ); // Optional: for debugging
    setIpinModalOpen(false);
    setOpenSetPasswordModal(false);

    if (pendingActionPayload) {
      await hitStatusChange(pendingActionPayload); // hitStatusChange will manage its own loading state
      setPendingActionPayload(null); // Clear the payload after action
    }
  };

  const handleCloseIpinModals = () => {
    // setLoading(false); // Stop loading if IPIN modal is cancelled by the user
    setIpinModalOpen(false);
    setOpenSetPasswordModal(false);
    setPendingActionPayload(null); // Clear payload if modals are closed manually
  };

  // Handle select/deselect for a single row
  const handleSelectRow = (externalRef) => {
    setSelectedRefs((prev) =>
      prev.includes(externalRef)
        ? prev.filter((ref) => ref !== externalRef)
        : [...prev, externalRef]
    );
  };

  // Handle select/deselect all
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRefs(data.map((item) => item.externalRef));
    } else {
      setSelectedRefs([]);
    }
  };

  // Bulk Approve/Reject/Manual Approve
  const handleBulkAction = (status) => {
    setLoading(true); // Start loading on button click
    if (selectedRefs.length === 0) {
      Swal.fire({
        title: "No Selection",
        text: "Please select at least one transaction.",
        icon: "warning",
      });
      return;
    }
    const payload = {
      Status: status,
      externalRefId: selectedRefs, // Array of externalRef
      service: payoutService,
    };
    triggerIpinVerification(payload);
  };

  // Row-wise Approve: directly trigger IPIN verification
  const handleRowApprove = (externalRef) => {
    setLoading(true); // Start loading when the row approve button is clicked
    const payload = {
      Status: "Approved",
      externalRefId: externalRef,
      service: payoutService,
    };
    triggerIpinVerification(payload);
  };

  // Row-wise Reject
  const handleRowReject = async (externalRef) => {
    console.log(
      "[ApprovalPayout] handleRowReject called with externalRef:",
      externalRef
    );
    setLoading(true); // Start loading on button click
    const payload = {
      Status: "Reject",
      externalRefId: externalRef,
      service: payoutService,
    };
    await triggerIpinVerification(payload);
  };

  // API call to change status
  const hitStatusChange = async (payload) => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("jwtToken");
      const url =
        "https://api.ipaisa.co.in/myapp/auth/instantPay/handleTransaction";

      const response = await axios.post(url, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      Swal.fire({
        title: "Transaction Status changed..!!",
        text: `${response.data.message}`,
        icon: "info",
        timer: 4000,
      });
      setSelectedRefs([]); // Clear selection after action
      fetchApprovalTxn(currPage, pageSize); // Ensure this is awaited before loading stops
    } catch (e) {
      console.error("Error changing status:", e);
      Swal.fire({
        title: "Error",
        text: "Something went wrong while updating the transaction.",
        icon: "error",
      });
      // setLoading(false) will be handled by finally
    } finally {
      setLoading(false); // Ensure loading is set to false in all outcomes
      fetchApprovalTxn(currPage, pageSize); // Ensure this is awaited before loading stops
    }
  };

  // Fetch data
  const fetchApprovalTxn = async (page, pageSize) => {
    const token = sessionStorage.getItem("jwtToken");
    const url =
      "https://api.ipaisa.co.in/myapp/auth/instantPay/getUnapprovedTxns";
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        params: {
          page,
          size: pageSize,
          fromDate: filters.startDate
            ? dayjs(filters.startDate).format("YYYY-MM-DD")
            : ``,
          toDate: filters.endDate
            ? dayjs(filters.endDate).format("YYYY-MM-DD")
            : ``,
          searchBy: filters.searchBy,
        },
      });
      setData(response.data?.data || []);
      setTotalElements(response.data?.totalElements || 0);
      return response.data;
    } catch (e) {}
  };

  // Pagination
  const handleChangePage = (event, newPage) => {
    setCurrPage(newPage);
    fetchApprovalTxn(newPage, pageSize);
  };

  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value));
    setCurrPage(0);
    fetchApprovalTxn(0, event.target.value);
  };

  // Filter menu
  const handleFilterMenuClick = (event) => {
    setFilterMenuAnchor(event.currentTarget);
  };
  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
  };

  // Date change
  const handleDateChange = (key, newValue) => {
    if (!newValue || !newValue.isValid?.()) return;
    let dateWithTime = newValue;
    if (key) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        [key]: dateWithTime ? dayjs(dateWithTime).format("YYYY-MM-DD") : "",
      }));
    }
  };

  // Handler for search text change
  const handleSearchChange = (event) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      searchBy: event.target.value,
    }));
  };

  // Apply filters
  const applyFilters = () => {
    handleFilterMenuClose();
    if (
      filters.endDate &&
      filters.startDate &&
      dayjs(filters.endDate).isBefore(dayjs(filters.startDate))
    ) {
      Swal.fire({
        title: "Something Went Wrong..!!",
        text: "End Date cannot be less than Start Date.",
        icon: "error",
      });
      return;
    } else {
      fetchApprovalTxn(currPage, pageSize);
    }
  };

  // Download
  const handleClickOpenDownloadWindow = () => {
    setOpenDownloadWindow(true);
  };
  const handleCloseDownloadWindow = () => {
    setOpenDownloadWindow(false);
  };

  const handleDownload = async () => {
    setLoading(true);
    let allData = [];
    var page = 0;
    var downloadSize = 1000;
    let hasNext = true;
    try {
      while (hasNext) {
        var response = await fetchApprovalTxn(page, downloadSize);
        if (response.data && response.data.length > 0) {
          allData = [...allData, ...response.data];
        }
        hasNext = response.hasNext;
        page++;
      }
      if (allData.length > 0) {
        downloadExcel(allData);
      } else {
        alert("No data to download");
        setLoading(false);
      }
    } catch (e) {
      setLoading(false);
    }
  };

  // Excel download function
  const downloadExcel = (data) => {
    if (!data || data.length === 0) {
      alert("No data to download");
      setLoading(false);
      return;
    }
    try {
      const currentDateTime = dayjs().format("YYYY-MM-DD_HH-mm-ss");
      const columnMappings = {
        payerName: "IFSC",
        payeeAccount: "ACC. NO",
        payeeName: "PAYEE NAME",
        txnValue: "FINAL AMT",
        transfer_mode: "TRX TYPE",
        utr: "UTR",
        bankName: "BANK",
        externalRef: "TRX ID",
        userId: "USER ID",
      };
      const transformedData = data.map((item) => {
        const newItem = {};
        Object.keys(columnMappings).forEach((originalKey) => {
          if (item.hasOwnProperty(originalKey)) {
            newItem[columnMappings[originalKey]] = item[originalKey];
          }
        });
        return newItem;
      });
      const ws = writeXLSX.utils.json_to_sheet(transformedData);
      const wb = writeXLSX.utils.book_new();
      writeXLSX.utils.book_append_sheet(wb, ws, "Approval Transactions");
      writeXLSX.writeFile(wb, `approval_transactions-${currentDateTime}.xlsx`);
    } catch (error) {
      alert("Failed to generate Excel file");
    }
    fetchApprovalTxn(0, pageSize);
    setCurrPage(0);
    handleCloseDownloadWindow();
    setLoading(false);
  };

  useEffect(() => {
    const fetchService = sessionStorage.getItem("payoutService");
    setPayoutService(fetchService);
    fetchApprovalTxn(0, 25);
  }, []);

  return (
    <>
      <div style={{ minHeight: "100vh", backgroundColor: "#F2F5FA" }}>
        <Container maxWidth style={{ marginTop: "10%", padding: "0px 50px" }}>
          <Typography
            variant="h4"
            align="start"
            gutterBottom
            style={{ fontWeight: "bold" }}
          >
            Approval Payouts
          </Typography>
          {/* Filter Button with Icon */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            marginTop={2}
          >
            <IconButton onClick={handleFilterMenuClick}>
              <FilterAlt />
            </IconButton>
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton onClick={handleClickOpenDownloadWindow}>
                <Typography>Download Report </Typography> <Download />
              </IconButton>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleBulkAction("Modified")}
                disabled={selectedRefs.length === 0 || loading}
                sx={{ mr: 1 }}
              >
                Manual Approve
              </Button>

              {/* <Button
              variant="contained"
              color="primary"
              onClick={() => handleBulkAction("Approved")}
              disabled={selectedRefs.length === 0 || loading}
              sx={{ mr: 1 }}
            >
              Approve
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleBulkAction("Reject")}
              disabled={selectedRefs.length === 0 || loading}
            >
              Reject
            </Button> */}
            </Box>
            <Menu
              anchorEl={filterMenuAnchor}
              open={Boolean(filterMenuAnchor)}
              onClose={handleFilterMenuClose}
              disableAutoFocus
              disableEnforceFocus
            >
              <MenuItem>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MobileDatePicker
                    label="Start Date"
                    value={filters.startDate ? dayjs(filters.startDate) : null}
                    onKeyDown={(e) => e.stopPropagation()}
                    onChange={(newValue) =>
                      handleDateChange("startDate", newValue)
                    }
                    sx={{ width: "100%" }}
                  />
                </LocalizationProvider>
              </MenuItem>
              <MenuItem>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MobileDatePicker
                    label="End Date"
                    value={filters.endDate ? dayjs(filters.endDate) : null}
                    onChange={(newValue) =>
                      handleDateChange("endDate", newValue)
                    }
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{ width: "100%" }}
                  />
                </LocalizationProvider>
              </MenuItem>
              <MenuItem>
                <TextField
                  label="Search"
                  variant="outlined"
                  value={filters.searchBy}
                  onChange={handleSearchChange}
                  onKeyDown={(e) => e.stopPropagation()}
                  sx={{ width: "100%", mt: 1 }}
                />
              </MenuItem>
              <MenuItem>
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  sx={{ width: "100%" }}
                >
                  Apply Filters
                </Button>
              </MenuItem>
            </Menu>
          </Box>

          <Paper>
            <TableContainer>
              <Table>
                <TableHead
                  style={{
                    backgroundColor: "#027BFE",
                    color: "white",
                  }}
                >
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="default"
                        sx={{
                          color: "common.white",
                          "&.Mui-checked": {
                            color: "common.white",
                          },
                          "&.MuiCheckbox-indeterminate": {
                            color: "common.white",
                          },
                          "& .MuiSvgIcon-root": {},
                        }}
                        indeterminate={
                          selectedRefs.length > 0 &&
                          selectedRefs.length < data.length
                        }
                        checked={
                          data.length > 0 && selectedRefs.length === data.length
                        }
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell
                      style={{
                        fontWeight: "bold",
                        color: "white",
                      }}
                      align="center"
                    >
                      Sr. No.
                    </TableCell>
                    <TableCell
                      style={{
                        fontWeight: "bold",
                        color: "white",
                      }}
                      align="center"
                    >
                      txnId
                    </TableCell>
                    <TableCell
                      style={{
                        fontWeight: "bold",
                        color: "white",
                      }}
                      align="center"
                    >
                      User ID
                    </TableCell>
                    <TableCell
                      style={{
                        fontWeight: "bold",
                        color: "white",
                      }}
                      align="center"
                    >
                      User Name
                    </TableCell>
                    <TableCell
                      style={{
                        fontWeight: "bold",
                        color: "white",
                      }}
                      align="center"
                    >
                      Account Number
                    </TableCell>
                    <TableCell
                      style={{
                        fontWeight: "bold",
                        color: "white",
                      }}
                      align="center"
                    >
                      Account IFSC
                    </TableCell>
                    <TableCell
                      style={{
                        fontWeight: "bold",
                        color: "white",
                      }}
                      align="center"
                    >
                      Date & Time
                    </TableCell>
                    <TableCell
                      style={{
                        fontWeight: "bold",
                        color: "white",
                      }}
                      align="center"
                    >
                      Wallet Opening Balance
                    </TableCell>
                    <TableCell
                      style={{
                        fontWeight: "bold",
                        color: "white",
                      }}
                      align="center"
                    >
                      txn Amount
                    </TableCell>
                    <TableCell
                      style={{
                        fontWeight: "bold",
                        color: "white",
                      }}
                      align="center"
                    >
                      Wallet Closing Balance
                    </TableCell>
                    <TableCell
                      style={{
                        fontWeight: "bold",
                        color: "white",
                      }}
                      align="center"
                    >
                      Txn Status
                    </TableCell>
                    <TableCell
                      style={{
                        fontWeight: "bold",
                        color: "white",
                      }}
                      align="center"
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data && data.length > 0 ? (
                    data.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={selectedRefs.includes(item.externalRef)}
                            onChange={() => handleSelectRow(item.externalRef)}
                          />
                        </TableCell>
                        <TableCell align="center">{index + 1}</TableCell>
                        <TableCell align="center">{item.externalRef}</TableCell>
                        <TableCell align="center">
                          {item.userdetails?.userid}
                        </TableCell>
                        <TableCell align="center">
                          {item.userdetails?.name}
                        </TableCell>
                        <TableCell align="center">
                          {item.payeeAccount}
                        </TableCell>
                        <TableCell align="center">{item.payerName}</TableCell>
                        <TableCell align="center">{item.timestamp}</TableCell>
                        <TableCell align="center">
                          {item.walletOpeningBalance}
                        </TableCell>
                        <TableCell align="center">{item.txnValue}</TableCell>
                        <TableCell align="center">
                          {item.walletClosingBalance}
                        </TableCell>
                        <TableCell align="center">{item.status}</TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1}>
                            <LoadingButton
                              loading={loading}
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => handleRowApprove(item.externalRef)}
                              sx={{ mr: 1 }}
                            >
                              Approve
                            </LoadingButton>
                            <LoadingButton
                              loading={loading}
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={() => handleRowReject(item.externalRef)}
                            >
                              Reject
                            </LoadingButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={13}
                        align="center"
                        style={{
                          padding: "20px",
                          fontStyle: "italic",
                        }}
                      >
                        No data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={totalElements}
              rowsPerPage={pageSize}
              page={currPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Container>
        {/* Download Dialog */}
        <Dialog
          onClick={handleClickOpenDownloadWindow}
          open={openDownloadWindow}
          onClose={handleCloseDownloadWindow}
        >
          <DialogTitle id="responsive-dialog-title">
            Download Pending Payouts Report
          </DialogTitle>
          <DialogContent>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDateTimePicker
                sx={{ width: "100%", marginTop: 2 }}
                label="Start Date"
                value={filters.startDate ? dayjs(filters.startDate) : null}
                onKeyDown={(e) => e.stopPropagation()}
                onChange={(newValue) => handleDateChange("startDate", newValue)}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDateTimePicker
                sx={{ width: "100%", marginTop: 2 }}
                label="End Date"
                value={filters.endDate ? dayjs(filters.endDate) : null}
                onChange={(newValue) => handleDateChange("endDate", newValue)}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </LocalizationProvider>
          </DialogContent>
          <DialogActions>
            <LoadingButton autoFocus loading={loading} onClick={handleDownload}>
              <Download /> Download
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </div>

      {/* IPIN Modals */}
      {(() => {
        const isTxnMpinSet = sessionStorage.getItem("isTxnMpinSet");
        console.log(
          "[ApprovalPayout] Rendering modals. isIpinModalOpen:",
          isIpinModalOpen,
          "openSetPasswordModal:",
          openSetPasswordModal,
          "sessionStorage isTxnMpinSet:",
          isTxnMpinSet
        );
        if (isTxnMpinSet === "False") {
          return (
            <SetPasswordModal
              open={openSetPasswordModal}
              onClose={handleCloseIpinModals} // Use the combined close handler
              onMpinValid={handleIpinVerificationSuccess} // Use the new success handler
            />
          );
        } else {
          return (
            <IpinModal
              open={isIpinModalOpen}
              onClose={handleCloseIpinModals} // Use the combined close handler
              onMpinValid={handleIpinVerificationSuccess} // Use the new success handler
            />
          );
        }
      })()}
    </>
  );
};

export default ApprovalPayout;
